import { useEffect, useState } from 'react';
import { portailApi } from '../../api/resources';
import { tr } from '../../lib/i18n';
import { C } from '../../theme/palette';
import { libelleChantier } from './listes';
import { alerte, bouton, carte, champ, etiquette } from './styles';

const VIDE = { numero: '', nom: '', adresse: '', precision: '' };

/**
 * « 🏗 Mes chantiers » : les chantiers de l'entreprise, communs à tous ses
 * fournisseurs. Le responsable ou un chef les ajoute — numéro, nom, adresse
 * exacte de livraison, précision pour le livreur —, les corrige, et retire
 * d'un appui un chantier terminé. L'ouvrier les choisit en commandant.
 *
 * @param {object} props propriétés
 * @param {string} [props.client] entreprise cliente
 * @returns {import('react').ReactElement} les chantiers
 */
export function MesChantiers({ client }) {
  const [chantiers, setChantiers] = useState(null);
  const [f, setF] = useState(VIDE);
  const [modifie, setModifie] = useState(null);
  const [etat, setEtat] = useState({ enCours: false, erreur: '' });
  const [erreurListe, setErreurListe] = useState('');
  const recharge = () =>
    portailApi
      .chantiers()
      .then((l) => setChantiers(Array.isArray(l) ? l : []))
      .catch(() => setChantiers((c) => c || []));
  useEffect(() => {
    recharge();
  }, []);
  const maj = (cle) => (e) => setF((x) => ({ ...x, [cle]: e.target.value }));
  const enregistre = async (e) => {
    e.preventDefault();
    if (!f.nom.trim() || !f.adresse.trim()) {
      setEtat({
        enCours: false,
        erreur: tr('Indiquez le nom du chantier et l’adresse exacte de livraison.'),
      });
      return;
    }
    setEtat({ enCours: true, erreur: '' });
    const chantier = {
      numero: f.numero.trim() || null,
      nom: f.nom.trim(),
      adresse: f.adresse.trim(),
      precision: f.precision.trim() || null,
    };
    try {
      if (modifie) await portailApi.modifieChantier(modifie, chantier);
      else await portailApi.ajouteChantier(chantier);
      setF(VIDE);
      setModifie(null);
      setEtat({ enCours: false, erreur: '' });
      recharge();
    } catch (err) {
      setEtat({
        enCours: false,
        erreur: err?.message || tr('Le chantier n’a pas pu être enregistré. Réessayez.'),
      });
    }
  };
  const termine = async (ch) => {
    setErreurListe('');
    try {
      await portailApi.termineChantier(ch.id);
    } catch (err) {
      setErreurListe(err?.message || tr('Ça n’a pas marché. Réessayez.'));
    }
    recharge();
  };
  return (
    <section aria-labelledby="titre-chantiers">
      <h2 id="titre-chantiers" style={{ fontSize: 18, margin: '4px 0 2px' }}>
        🏗 Mes chantiers
      </h2>
      {client && <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>{client}</div>}
      <form style={{ ...carte, border: '2px solid ' + C.teal }} onSubmit={enregistre}>
        <b style={{ display: 'block', fontSize: 15, marginBottom: 8 }}>
          {modifie ? tr('Modifier le chantier') : tr('Ajouter un chantier')}
        </b>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
          <label style={etiquette}>
            N° de chantier
            <input
              style={{ ...champ, minWidth: 0 }}
              maxLength={40}
              placeholder={tr('ex. 2026-021')}
              value={f.numero}
              onChange={maj('numero')}
            />
          </label>
          <label style={etiquette}>
            Nom
            <input
              style={{ ...champ, minWidth: 0 }}
              maxLength={160}
              value={f.nom}
              onChange={maj('nom')}
            />
          </label>
        </div>
        <label style={{ ...etiquette, marginTop: 8 }}>
          Adresse exacte de livraison
          <input
            style={champ}
            maxLength={200}
            placeholder={tr('Rue, n°, NPA, localité')}
            value={f.adresse}
            onChange={maj('adresse')}
          />
        </label>
        <label style={{ ...etiquette, marginTop: 8 }}>
          Précision pour le livreur (facultatif)
          <input
            style={champ}
            maxLength={95}
            placeholder={tr('ex. Entrée côté parking, grue jaune')}
            value={f.precision}
            onChange={maj('precision')}
          />
        </label>
        {etat.erreur && (
          <div role="alert" style={alerte}>
            {etat.erreur}
          </div>
        )}
        <button
          type="submit"
          disabled={etat.enCours}
          style={{ ...bouton(C.green), width: '100%', marginTop: 10 }}
        >
          {modifie ? tr('✓ Enregistrer') : tr('＋ Ajouter le chantier')}
        </button>
        {modifie && (
          <button
            type="button"
            onClick={() => {
              setModifie(null);
              setF(VIDE);
            }}
            style={{ ...bouton(C.navy, false), width: '100%', marginTop: 8 }}
          >
            Annuler
          </button>
        )}
      </form>
      {chantiers === null && <div style={{ ...carte, color: C.muted }}>Chargement…</div>}
      {erreurListe && (
        <div role="alert" style={alerte}>
          {erreurListe}
        </div>
      )}
      {chantiers && chantiers.length === 0 && (
        <div style={{ ...carte, textAlign: 'center', color: C.muted }}>
          Aucun chantier pour l’instant.
        </div>
      )}
      {(chantiers || []).map((ch) => (
        <div key={ch.id} data-testid={'chantier-' + ch.id} style={carte}>
          <b style={{ display: 'block', fontSize: 14 }} translate="no">
            {libelleChantier(ch.numero, ch.nom)}
          </b>
          <div style={{ fontSize: 12, color: C.grey }} translate="no">
            {ch.adresse}
          </div>
          {ch.precision && (
            <div style={{ fontSize: 12, color: C.muted }} translate="no">
              {ch.precision}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button
              onClick={() => {
                setModifie(ch.id);
                setF({
                  numero: ch.numero || '',
                  nom: ch.nom || '',
                  adresse: ch.adresse || '',
                  precision: ch.precision || '',
                });
                setEtat({ enCours: false, erreur: '' });
              }}
              aria-label={tr('Modifier') + ' ' + libelleChantier(ch.numero, ch.nom)}
              style={{ ...bouton(C.navy, false), flex: 1, padding: '8px 10px', fontSize: 13 }}
            >
              Modifier
            </button>
            <button
              onClick={() => termine(ch)}
              aria-label={tr('Chantier terminé') + ' ' + libelleChantier(ch.numero, ch.nom)}
              style={{ ...bouton(C.green, false), flex: 1, padding: '8px 10px', fontSize: 13 }}
            >
              ✓ Terminé
            </button>
          </div>
        </div>
      ))}
      <div
        style={{
          background: '#E8F3EC',
          borderRadius: 12,
          padding: '10px 12px',
          fontSize: 12,
          color: '#14532D',
          lineHeight: 1.5,
        }}
      >
        Vos chantiers valent chez tous vos fournisseurs. En commandant, on choisit le chantier : son
        adresse et son numéro partent avec la commande. « Terminé » le retire de la liste.
      </div>
    </section>
  );
}
